import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Rocket, Target, BarChart3, Brain, ChevronDown, ChevronUp, MessageCircle } from "lucide-react";

import Sidebar from "../components/Sidebar";
import Loader from "../components/loader/Loader";
import ChatUI from "../components/chatUI/ChatUI";
import CareerDetails from "../components/recommendCareers/CareerDetails";
import ActionCard from "../components/card/ActionCard";

import { openChat } from "../redux/chatSlice";
import useMainstreamCareers from "../utils/useMainstreamCareers";
import useEmergingAI from "../utils/useEmergingAI";
import { HEADING_COLOR } from "../constants/colors";
import { COLORS } from "../constants/colors";
import { api1 } from "../utils/axiosInstance";
import { API_ENDPOINTS } from "../services/apiEndpoints";

const TABS = ['emerging', 'mainstream'];
const CAREER_TYPES = ['Similar', 'Advancement', 'Lateral Move'];

const processCareers = (data, isEmerging = false) => {
  const fields = isEmerging
    ? ['top_3_similar_ai_emerging_occupations', 'top_3_advance_ai_emerging_occupations', 'top_3_lateral_ai_emerging_occupations']
    : ['top_3_similar_occupations', 'top_3_advance_occupations', 'top_3_lateral_occupations'];
  return data
    ? fields.flatMap(f => data[f] || []).filter((c, i, a) => i === a.findIndex(x => x.name === c.name)).slice(0, 3)
    : [];
};

const CareerRecommendations = () => {
  const dispatch = useDispatch(), navigate = useNavigate(), queryClient = useQueryClient();
  const { currentJob } = useSelector(s => s.user), userId = useSelector(s => s.auth.id);
  const [activeTab, setActiveTab] = useState('emerging'), [expandedCareer, setExpandedCareer] = useState(null);
  const [savedCareers, setSavedCareers] = useState([]);
  const [loadingSavedCareers, setLoadingSavedCareers] = useState(false);

  const { data: mainstreamData, isPending: loadingMainstream } = useMainstreamCareers(currentJob);
  const { data: emergingData, isLoading: loadingEmerging } = useEmergingAI(currentJob);

  const careers = useMemo(() =>
    processCareers(activeTab === 'emerging' ? emergingData?.response : mainstreamData, activeTab === 'emerging'),
    [activeTab, mainstreamData, emergingData]);

  const fetchSavedCareers = async () => {
    if (!userId) return;
    
    try {
      setLoadingSavedCareers(true);
      const response = await api1.get(`${API_ENDPOINTS.GET_CAREER_BOOKMARKS}/${userId}`);
      const savedCareersData = response.data.data || [];
      setSavedCareers(savedCareersData);
    } catch (error) {
      console.error("Error fetching saved careers:", error);
      setSavedCareers([]);
    } finally {
      setLoadingSavedCareers(false);
    }
  };

  useEffect(() => {
    if (currentJob) {
      queryClient.invalidateQueries({ queryKey: ["mainstreamCareers"] });
      queryClient.invalidateQueries({ queryKey: ["emergingAIGraph"] });
    }
  }, [currentJob, queryClient]);

  useEffect(() => {
    fetchSavedCareers();
  }, [userId]);


  // Helper function to check if a career is saved
  const isCareerSaved = (career) => {
    if (!career || !savedCareers.length) return false;
    
    // Check by career name and type
    const careerType = career.necessarySkills && !career.tools_and_technologies ? "mainstream" : "emerging";
    
    return savedCareers.some(saved => {
      // Match by career name and type
      const nameMatch = saved.careerName === career.name || saved.title === career.name;
      const typeMatch = saved.careerType === careerType || saved.category === careerType;
      
      return nameMatch && typeMatch;
    });
  };

  if (loadingMainstream || loadingEmerging) return <Loader />

  return (
    <Sidebar>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6">

        {/* Header & Tabs */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#6e28cc' }}><Rocket className="w-5 h-5 text-white" /></div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-blue-900">
                {activeTab === 'emerging' ? 'Recommended Emerging AI Careers' : 'Recommended Mainstream Careers'}
              </h1>
              <p className="text-sm sm:text-base text-gray-500">
                {activeTab === 'emerging' ? 'High-growth opportunities in AI' : 'Stable, established opportunities'}
              </p>
            </div>
          </div>
          <div className="flex bg-gray-200 rounded-lg border p-1">
            {TABS.map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`px-4 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                  activeTab === tab ? 'bg-white text-black' : 'text-gray-700 hover:text-gray-900'}`}>
                {tab[0].toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Career Cards */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 border border-gray-300">
          {careers.length ? (
            <div className="space-y-4">
              {careers.map((career, i) => {
                const id = career._id || i, isExpanded = expandedCareer === id;
                return (
                  <div key={id} className="bg-gray-50 rounded-lg border">
                    <div className="p-3 sm:p-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      <div>
                        <h3 className="font-semibold text-gray-900">{career.name} - {CAREER_TYPES[i % 3]}</h3>
                      </div>
                      <button onClick={() => setExpandedCareer(isExpanded ? null : id)}
                        className="text-white px-3 py-2 sm:py-1 rounded-lg text-sm font-medium flex items-center gap-2"
                style={{ backgroundColor: '#6e28cc' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#5a1fb8'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#6e28cc'}>
                        Details {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                    <CareerDetails 
                      career={career} 
                      isExpanded={isExpanded} 
                      userId={userId} 
                      isSaved={isCareerSaved(career)}
                      onBookmarkChange={fetchSavedCareers}
                    />
                  </div>
                );
              })}
              <button onClick={() => navigate(`/dashboard/${activeTab === 'emerging' ? 'ai-careers' : 'mainstream-careers'}`)}
                className="w-full py-1 rounded-lg border font-medium text-gray-700 hover:bg-gray-50 text-center">
                View All {activeTab === 'emerging' ? 'Emerging' : 'Mainstream'} Careers
              </button>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No {activeTab} careers available at the moment.</p>
              <p>Check back later for updates.</p>
            </div>
          )}
        </div>

        {/* Action Cards */}
        <div className="space-y-3">
          <p className="text-xl sm:text-xl font-bold text-blue-900">Career Exploration Tools</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ActionCard icon={Brain} title="Want to assess your Skills for a Career?" description="Identify skill gaps between your current abilities and target career requirements." buttonText="Try the Skills Gap Analysis Tool" onClick={() => navigate('/dashboard/skills-and-learning/skill-gap-analysis-tool')} gradientIcon="bg-gradient-to-r from-purple-500 to-blue-500" gradientBtn="bg-gradient-to-r from-pink-500 to-purple-500" variant="outline" />
            <ActionCard icon={Target} title="Not Sure Which Career?" description="Try our interactive Career Simulator to practice skills and tools." buttonText="Try the Career Simulator" onClick={() => navigate('/dashboard/career-discovery/career-simulation-tool')} gradientIcon="bg-gradient-to-r from-purple-500 to-blue-500" gradientBtn="bg-gradient-to-r from-pink-500 to-purple-500" variant="outline" comingSoon />
            <ActionCard icon={BarChart3} title="Need to do a Quick Career Comparison?" description="Compare multiple careers side by side to make informed decisions." buttonText="Coming Soon" gradientIcon="bg-gradient-to-r from-purple-500 to-blue-500" gradientBtn="bg-gradient-to-r from-pink-500 to-purple-500" comingSoon disabled />
            <ActionCard icon={MessageCircle} title="Want to do a Mock Interview for a Career?" description="Practice interview questions and get feedback for your target career." buttonText="Try Mock Interview" onClick={() => window.open('https://intervia.ai/', '_blank')} gradientIcon="bg-gradient-to-r from-purple-500 to-blue-500" gradientBtn="bg-gradient-to-r from-pink-500 to-purple-500" variant="outline" />
          </div>
        </div>

        {/* Chat Button */}
        <button onClick={() => dispatch(openChat())}
            className="fixed bottom-6 right-6 w-14 h-14 text-white rounded-full shadow-lg flex items-center justify-center transform hover:scale-105 z-50"
            style={{ backgroundColor: COLORS.purple }}
            onMouseEnter={(e) => e.target.style.backgroundColor = COLORS.purpleHover}
            onMouseLeave={(e) => e.target.style.backgroundColor = COLORS.purple}>
          <MessageCircle className="w-6 h-6" />
        </button>

        <ChatUI />
      </div>
    </Sidebar>
  );
};

export default CareerRecommendations;
